import {
  KdsModifierStyle,
  ModifierKeywordMatchMode,
} from '@oolio-group/domain';
import { MODIFIER_STYLE_COLOURS } from '../common/modifierStyleTypes';

export const MAX_MODIFIER_STYLES = 3;
export const MAX_CONDITIONS_PER_STYLE = 10;
export const MAX_NAME_LENGTH = 30;

// Mirrors the server's `VALID_MODIFIER_COLORS` whitelist so an out-of-palette
// colour is rejected before the mutation round-trip rather than after it.
const VALID_MODIFIER_COLORS = new Set(
  MODIFIER_STYLE_COLOURS.map(colour => colour.color.toUpperCase()),
);

/**
 * A validation failure expressed as a translation key (under the
 * `kitchenDisplay` namespace) plus interpolation params, so the UI can render
 * a localized message. `matchType`, when present, is the raw enum for the UI
 * to translate into a match-type label.
 */
export interface ModifierStyleValidationError {
  key: string;
  params?: Record<string, string | number>;
  matchType?: ModifierKeywordMatchMode;
}

/**
 * Validates a single modifier style being created or edited, against the rest
 * of the device's existing styles. Mirrors `validateModifierStyles` in
 * `services/venues/src/utils/validation.ts` (the eventual server-side source
 * of truth) so invalid input is caught before a mutation round-trip.
 *
 * Returns `null` when valid, otherwise a translation key + params.
 */
export function validateModifierStyle(
  candidate: KdsModifierStyle,
  existingStyles: KdsModifierStyle[],
  isCreate: boolean,
): ModifierStyleValidationError | null {
  const name = (candidate.name || '').trim();
  if (!name) {
    return { key: 'modifierStyleErrorNameRequired' };
  }
  if (name.length > MAX_NAME_LENGTH) {
    return {
      key: 'modifierStyleErrorNameTooLong',
      params: { max: MAX_NAME_LENGTH },
    };
  }

  const others = existingStyles.filter(style => style.id !== candidate.id);

  if (isCreate && others.length + 1 > MAX_MODIFIER_STYLES) {
    return {
      key: 'modifierStyleErrorTooManyStyles',
      params: { max: MAX_MODIFIER_STYLES },
    };
  }

  const normalizedName = name.toLowerCase();
  if (
    others.some(style => style.name.trim().toLowerCase() === normalizedName)
  ) {
    return { key: 'modifierStyleErrorNameDuplicate', params: { name } };
  }

  const color = candidate.styles?.color;
  const italic = candidate.styles?.italic;
  if (!color && !italic) {
    return { key: 'modifierStyleErrorNoAppearance', params: { name } };
  }
  if (color && !VALID_MODIFIER_COLORS.has(color.toUpperCase())) {
    return { key: 'modifierStyleErrorInvalidColour', params: { name } };
  }

  const conditions = candidate.conditions || [];
  if (conditions.length === 0) {
    return { key: 'modifierStyleErrorNoKeyword', params: { name } };
  }
  if (conditions.length > MAX_CONDITIONS_PER_STYLE) {
    return {
      key: 'modifierStyleErrorTooManyKeywords',
      params: { name, max: MAX_CONDITIONS_PER_STYLE },
    };
  }

  const seen = new Set<string>();
  for (const condition of conditions) {
    const keyword = (condition?.keyword || '').trim();
    if (!keyword) {
      return { key: 'modifierStyleErrorKeywordRequired' };
    }
    if (keyword.length > MAX_NAME_LENGTH) {
      return {
        key: 'modifierStyleErrorKeywordTooLong',
        params: { max: MAX_NAME_LENGTH },
      };
    }
    const dedupeKey = `${keyword.toLowerCase()}::${condition.matchType}`;
    if (seen.has(dedupeKey)) {
      return {
        key: 'modifierStyleErrorDuplicateKeyword',
        params: { name, keyword: condition.keyword },
        matchType: condition.matchType,
      };
    }
    seen.add(dedupeKey);
  }

  return null;
}
